import { useState } from 'react';

import { DndContext, UniqueIdentifier, DragOverlay } from '@dnd-kit/core';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { DreConfigConfiguracoesItem, DreConfigConfiguracoesLine } from 'pages/financeiro/dre/configuracao/components';
import { DreConfigConfiguracoesTitle } from 'pages/financeiro/dre/configuracao/components/configuracoes/DreConfigConfiguracoesTitle';
import { useDreConfigConfiguracoesContext } from 'pages/financeiro/dre/configuracao/contexts/DreConfigConfiguracoesContext';
import { useDragConfiguracoes } from 'pages/financeiro/dre/configuracao/hooks';
import { DreConfiguracao } from 'pages/financeiro/dre/configuracao/types';

export function DreConfigConfiguracoesList({ title = true }: { title?: boolean }) {
    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
    const { configuracoes, isFetchingConfiguracoes } = useDreConfigConfiguracoesContext();
    const { lines, handleDragOver, handleDragEnd, getIdFromConfiguracao, sensors, customCollisionDetectionAlgorithm } = useDragConfiguracoes();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                padding: 1.25,
                paddingTop: title ? 1 : 1.25,
            }}
        >
            {title && <DreConfigConfiguracoesTitle />}

            {isFetchingConfiguracoes && (
                <Box
                    className="pulse"
                    sx={{
                        height: 86,
                        borderWidth: 1,
                        borderRadius: 1,
                        borderColor: 'grey.900',
                        backgroundColor: 'grey.200',
                    }}
                />
            )}

            {!isFetchingConfiguracoes && Boolean(configuracoes.length) && (
                <DndContext
                    sensors={sensors}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    collisionDetection={customCollisionDetectionAlgorithm}
                    onDragStart={({ active }) => setActiveId(active.id)}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            borderColor: 'grey.300',
                            borderStyle: 'solid',
                            borderWidth: 1,
                            borderRadius: 1,
                        }}
                    >
                        {Array.from(lines.entries()).map(([lineId, lineConfiguracoes]) => (
                            <DreConfigConfiguracoesLine
                                key={lineId}
                                lineId={lineId}
                                last={lineId === [...lines.keys()].pop()}
                                configuracoes={lineConfiguracoes}
                            />
                        ))}

                        {activeId && (
                            <DragOverlay>
                                <DreConfigConfiguracoesItem
                                    configuracao={
                                        configuracoes.find(
                                            (config) => config.idDreMercadologicaConfiguracao === getIdFromConfiguracao(activeId as UniqueIdentifier),
                                        ) as DreConfiguracao
                                    }
                                />
                            </DragOverlay>
                        )}
                    </Box>
                </DndContext>
            )}

            {!isFetchingConfiguracoes && !configuracoes.length && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        gap: 1,
                        padding: 1,
                        minHeight: 42,
                        border: 1,
                        borderRadius: 1,
                        borderStyle: 'solid',
                        borderColor: 'grey.300',
                    }}
                >
                    <InfoOutlined sx={{ fontSize: 16, color: 'grey.500' }} />
                    <Typography sx={{ fontSize: 13, color: 'grey.500' }}>Sem configurações adicionadas</Typography>
                </Box>
            )}
        </Box>
    );
}
